import _ from "lodash";
import React, { isValidElement, cloneElement } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { TextInput, Tooltip } from "@toddle-edu/ds-web";
import { InformationFilled } from "@toddle-edu/ds-icons";
import UIBaseComponent from "UIComponents/UIBaseComponent";

import {
  updateAssessment,
  updateAssessmentInRedux,
  writeAssessmentWeightageInCache,
} from "Assessments/modules/AssessmentModule";
import { getScoreDetailsMemoize } from "ScoreCategories/modules/ScoreCategoriesModule";
import { DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX } from "Constants";
import { GRADE_SCALE_TOOL } from "Constants/stringConstants";
import { getIsScoringToolPresent, getIsToolPresent } from "Utils";

const styles = {
  textInputStyles: {
    width: "auto",
    maxWidth: "144px",
  },
  overlayInnerStyle: {
    maxWidth: "264px",
    padding: "8px",
    textAlign: "center",
    zIndex: "1",
  },
};

class TaskWeightV2 extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      taskWeight: "",
      isError: false,
    };

    if (props?.customRef) {
      props.customRef(this);
    }
  }

  isValid = () => {
    const {
      assessmentTools,
      isCollapsibleAssessmentTool,
      isDPWithGradeScale,
      dpMaxScore,
    } = this.props;
    const { taskWeight, isError } = this.state;

    if (isCollapsibleAssessmentTool && !isDPWithGradeScale) {
      return 0;
    }

    // For DP with grade scale (proportional to max score):
    // show error only if both max score and task weight are not entered
    if (isDPWithGradeScale) {
      const isMaxScoreEmpty = _.isEmpty(_.toString(dpMaxScore));
      const isTaskWeightEmpty = _.isEmpty(_.toString(taskWeight));

      if (isMaxScoreEmpty && isTaskWeightEmpty) {
        this.setState({ isError: true });
        return 1;
      }

      if (isError) {
        this.setState({ isError: false });
      }
      return 0;
    }

    const { maxScore } = getScoreDetailsMemoize({
      assessmentTools,
    });

    if (!_.isEmpty(maxScore) && _.isEmpty(_.toString(taskWeight))) {
      this.setState({ isError: true });
      return 1;
    }

    if (isError) {
      this.setState({ isError: false });
    }
    return 0;
  };

  componentDidMount = () => {
    const { value, isDPWithGradeScale, dpMaxScore, mode } = this.props;
    const isDPMaxScoreFilled =
      isDPWithGradeScale && !_.isEmpty(_.toString(dpMaxScore));

    if (isDPMaxScoreFilled) {
      const parsedScore = parseFloat(dpMaxScore);
      this.updateState({ key: "taskWeight", value: _.toString(dpMaxScore) });
      this.debouncedUpdateWeightageInCache(parsedScore);

      // In edit mode, skip if backend already has the correct value
      const isSameAsExisting = _.isEqual(
        _.toString(dpMaxScore),
        _.toString(value)
      );
      if (!(mode === "edit" && isSameAsExisting)) {
        this.persistTaskWeight(parsedScore);
      }
    } else {
      this.updateState({ key: "taskWeight", value });
    }
  };

  componentDidUpdate = prevProps => {
    const { isDPWithGradeScale, dpMaxScore } = this.props;
    const isDPMaxScoreFilled =
      isDPWithGradeScale && !_.isEmpty(_.toString(dpMaxScore));
    const prevIsDPMaxScoreFilled =
      prevProps.isDPWithGradeScale &&
      !_.isEmpty(_.toString(prevProps.dpMaxScore));

    const { taskWeight } = this.state;
    const isSameValue = _.isEqual(
      _.toString(dpMaxScore),
      _.toString(taskWeight)
    );
    const hasMaxScoreChanged =
      !prevIsDPMaxScoreFilled || prevProps.dpMaxScore !== dpMaxScore;

    if (isDPMaxScoreFilled && hasMaxScoreChanged && !isSameValue) {
      const parsedScore = parseFloat(dpMaxScore);
      this.updateState({ key: "taskWeight", value: _.toString(dpMaxScore) });
      this.debouncedUpdateWeightageInCache(parsedScore);
    }
  };

  /**
   * Saves task weight (weightage) to the backend.
   * Skipped in view mode to avoid unintended mutations.
   *
   * In DP + grade-scale mode, scoreToolData is also included so the mutation
   * response returns the correct maxScore, preventing Apollo from overwriting
   * the cache with a stale value and causing a dpMaxScore flicker.
   */
  persistTaskWeight = weightage => {
    const {
      mode,
      updateAssessment,
      isDPWithGradeScale,
      dpMaxScore,
    } = this.props;
    if (mode === "view") return;

    const scoreToolData =
      isDPWithGradeScale && !_.isEmpty(_.toString(dpMaxScore))
        ? { isUsingScore: true, maxScore: _.toString(dpMaxScore) }
        : undefined;

    updateAssessment({ hasFields: false, weightage, scoreToolData });
  };

  updateState = ({ key, value }) => {
    this.setState(prevState => {
      return {
        ...prevState,
        [key]: value,
      };
    });
  };

  debouncedUpdateWeightageInCache = _.debounce(weightage => {
    const {
      writeAssessmentWeightageInCache,
      assessmentId,
      parentId,
    } = this.props;

    writeAssessmentWeightageInCache({
      assessmentId: assessmentId || parentId,
      weightage,
    });
  }, 300);

  updateTaskWeight = e => {
    const { isError } = this.state;
    const weight = _.get(e, "target.value");
    const weightFloat = parseFloat(weight);

    // allow only numbers and upto 2 decimals
    if (!DOUBLE_DIGIT_DECIMAL_NUMBERS_REGEX.test(weight) && weight) {
      return;
    }

    const weightage =
      weightFloat || !weight || _.size(weight) > 0 ? weight : "";

    this.updateState({
      key: "taskWeight",
      value: weightage,
    });

    if (isError) {
      this.setState({ isError: false });
    }

    this.debouncedUpdateWeightageInCache(weightage);
  };

  onBlur = () => {
    const { updateAssessment } = this.props;
    const { taskWeight } = this.state;

    if (_.includes(taskWeight, ".")) {
      this.updateState({
        key: "taskWeight",
        value: _.toString(parseFloat(taskWeight)),
      });
    }

    updateAssessment({
      hasFields: false,
      weightage: taskWeight ? parseFloat(taskWeight) : null,
    });
  };

  renderTaskWeight = () => {
    const {
      t,
      mode,
      testId,
      assessmentTools,
      isDPWithGradeScale,
      dpMaxScore,
      isCustomWeights,
    } = this.props;
    const { taskWeight, isError } = this.state;

    const isViewMode = mode === "view";

    const isScoringToolPresent = getIsScoringToolPresent(assessmentTools);

    // For DP with grade scale: if max score is entered, mirror it as task weight (disabled)
    const isDPMaxScoreFilled =
      isDPWithGradeScale && !_.isEmpty(_.toString(dpMaxScore));
    const displayValue = isDPMaxScoreFilled
      ? _.toString(dpMaxScore)
      : taskWeight;
    // Disable task weight when a scoring tool is present in proportional mode.
    // In custom weights mode the teacher sets weight explicitly, so keep it editable.
    const isScoringToolDisabled = isScoringToolPresent && !isCustomWeights;
    const disabled = isViewMode || isScoringToolDisabled || isDPMaxScoreFilled;

    return (
      <Tooltip
        tooltip={t("classRoom:only_one_scoring_based_tool_is_allowed_msg")}
        visible={isScoringToolDisabled}
        placement="top"
        dsVersion="2.0"
        overlayInnerStyle={styles.overlayInnerStyle}
      >
        <TextInput
          size={"medium"}
          placeholder={t("common:eg_100")}
          style={styles.textInputStyles}
          value={displayValue}
          error={isError}
          onChange={this.updateTaskWeight}
          onBlur={this.onBlur}
          disabled={disabled}
          testId={testId}
        />
      </Tooltip>
    );
  };

  renderEdit = this.renderTaskWeight;
  renderView = this.renderTaskWeight;

  render() {
    const { isCollapsibleAssessmentTool, assessmentTools } = this.props;
    // Hide the entire component (label + input) when a scoring tool or grade scale tool is
    // present and this is the standalone instance next to MaxScore.
    // CollapsibleAssessmentTools renders its own task weight below the tool in that case.
    if (
      !isCollapsibleAssessmentTool &&
      (getIsScoringToolPresent(assessmentTools) ||
        getIsToolPresent(assessmentTools, GRADE_SCALE_TOOL))
    ) {
      return null;
    }
    return super.render();
  }
}

const mapActionCreators = {
  updateAssessment,
  updateAssessmentInRedux,
  writeAssessmentWeightageInCache,
};

const mapStateToProps = (state, ownProps) => {
  const { t, labelComponent: _labelComponent, label, weightage } = ownProps;

  const labelRightIcon = (
    <Tooltip
      placement={"bottom"}
      tooltip={t("classRoom:task_weight_tooltip_message")}
      overlayInnerStyle={styles.overlayInnerStyle}
      showArrow={true}
    >
      <InformationFilled variant={"subtle"} size={"xxx-small"} />
    </Tooltip>
  );

  const labelComponent = isValidElement(_labelComponent)
    ? cloneElement(_labelComponent, {
        label,
        labelRightIcon,
      })
    : null;

  return {
    labelComponent,
    value: weightage,
  };
};

TaskWeightV2.propTypes = {
  ...UIBaseComponent.propTypes,
  isCollapsibleAssessmentTool: PropTypes.bool,
  isDPWithGradeScale: PropTypes.bool,
  isCustomWeights: PropTypes.bool,
};

TaskWeightV2.defaultProps = {
  ...UIBaseComponent.defaultProps,
  isCollapsibleAssessmentTool: false,
  isDPWithGradeScale: false,
  isCustomWeights: false,
};

export default connect(mapStateToProps, mapActionCreators)(TaskWeightV2);
